import React, { useState } from 'react';
import PublicLayout from '../../components/public/PublicLayout';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

const FAQsPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqCategories = [
    {
      category: 'Membership',
      icon: '👥',
      questions: [
        {
          question: 'Who can become an ARCA member?',
          answer: 'ARCA membership is open to anyone interested in supporting and advancing respiratory care in Africa. This includes respiratory therapists, healthcare professionals (doctors, nurses, physiotherapists), respiratory therapy students, educators, researchers, hospitals, universities, vendors, and anyone passionate about improving respiratory health across the continent.'
        },
        {
          question: 'What are the membership benefits?',
          answer: 'Members enjoy comprehensive benefits including: access to workshops, seminars, and monthly educational events (two per month across Africa); opportunities to earn continuing education credits (CEUs/CDEs); early access and discounted rates for the ARCA International Conference; networking with respiratory professionals across Africa and globally; members-only access to clinical protocols, guidelines, training manuals and instructional videos; eligibility for educational scholarships and research mini-grants; career guidance and mentorship programs; participation in committees and leadership roles; reduced fees for conferences and certification prep courses; and recognition through ARCA newsletters, awards, and professional spotlights.'
        },
        {
          question: 'How much does membership cost?',
          answer: 'Membership fees vary by category and country. Students receive discounted rates. Please visit our membership page or contact us for current pricing information.'
        },
        {
          question: 'How do I renew my membership?',
          answer: 'Membership renewal can be done through your member dashboard. You will receive email reminders 30 days before your membership expires.'
        }
      ]
    },
    {
      category: 'Training & Education',
      icon: '🎓',
      questions: [
        {
          question: 'What training programs does ARCA offer?',
          answer: 'ARCA offers online courses through our Virtual University, in-person workshops, webinars, certification preparation programs, and continuing education opportunities. Topics cover clinical skills, equipment operation, patient care, and professional development.'
        },
        {
          question: 'Are ARCA courses accredited?',
          answer: 'ARCA is working towards formal accreditation. Currently, we provide certificates of completion and CPD points that are recognized by many national respiratory therapy associations across Africa.'
        },
        {
          question: 'Can I access courses if I\'m not a member?',
          answer: 'Some introductory courses and webinars are available to non-members. However, full access to our Virtual University and advanced training requires ARCA membership.'
        }
      ]
    },
    {
      category: 'Events & Conferences',
      icon: '📅',
      questions: [
        {
          question: 'When is the next ARCA annual conference?',
          answer: 'Our annual conference details are announced 6 months in advance. Please check our Events page or subscribe to our newsletter for updates on upcoming conferences and registration information.'
        },
        {
          question: 'Can I present at ARCA conferences?',
          answer: 'Yes! We welcome abstracts for oral presentations, poster sessions, and workshops. Call for abstracts is typically opened 4-5 months before the conference. Members receive priority consideration.'
        },
        {
          question: 'Are virtual attendance options available?',
          answer: 'Yes, we offer hybrid conference formats with both in-person and virtual attendance options to ensure accessibility for members across the continent.'
        }
      ]
    },
    {
      category: 'Resources & Publications',
      icon: '📚',
      questions: [
        {
          question: 'How can I access ARCA publications?',
          answer: 'Members have full access to our research library, clinical guidelines, and publications through the Resources section of their member dashboard. Some publications are also available to the public.'
        },
        {
          question: 'Can I submit research for publication?',
          answer: 'Yes, ARCA accepts research submissions from members. We are developing our publication review process. Contact our research team for submission guidelines.'
        },
        {
          question: 'Are clinical guidelines available in multiple languages?',
          answer: 'We are working on translating key guidelines into French, Arabic, and Portuguese. Currently, most materials are in English.'
        }
      ]
    },
    {
      category: 'Technical Support',
      icon: '🔧',
      questions: [
        {
          question: 'I\'m having trouble accessing my account. What should I do?',
          answer: 'Try resetting your password using the "Forgot Password" link on the login page. If issues persist, contact our support team at support@africaresp.org with your membership number.'
        },
        {
          question: 'How do I update my profile information?',
          answer: 'Log into your member dashboard and navigate to Profile Settings. You can update your contact information, professional details, and preferences there.'
        },
        {
          question: 'Can I access ARCA resources on mobile devices?',
          answer: 'Yes, our website and member portal are fully responsive and optimized for mobile devices. We also recommend using modern browsers for the best experience.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const faqId = `${categoryIndex}-${questionIndex}`;
    setOpenFAQ(openFAQ === faqId ? null : faqId);
  };

  return (
    <PublicLayout>
      <div className="py-5">
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4" 
                   style={{ width: '80px', height: '80px', backgroundColor: '#6B8E23' }}>
                <HelpCircle className="text-white" size={40} />
              </div>
              <h1 className="display-4 fw-bold text-dark mb-3">Frequently Asked Questions</h1>
              <p className="lead text-muted">
                Find answers to common questions about ARCA membership, training, events, and more
              </p>
            </div>
          </div>

          {/* FAQ Categories */}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {faqCategories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-4">
                  <div className="card shadow-sm border-0">
                    <div className="card-header py-3" style={{ backgroundColor: '#e8f5e9', borderBottom: '2px solid #6B8E23' }}>
                      <h3 className="h5 fw-bold text-dark mb-0">
                        <span className="me-2">{category.icon}</span>
                        {category.category}
                      </h3>
                    </div>
                    <div className="card-body p-0">
                      {category.questions.map((faq, questionIndex) => {
                        const faqId = `${categoryIndex}-${questionIndex}`;
                        const isOpen = openFAQ === faqId;
                        
                        return (
                          <div key={questionIndex} className="border-bottom">
                            <button
                              className="btn btn-link text-start w-100 text-decoration-none p-4 d-flex justify-content-between align-items-center"
                              onClick={() => toggleFAQ(categoryIndex, questionIndex)}
                              style={{ color: '#333' }}
                            >
                              <span className="fw-semibold">{faq.question}</span>
                              {isOpen ? (
                                <ChevronUp size={20} style={{ color: '#6B8E23' }} />
                              ) : (
                                <ChevronDown size={20} style={{ color: '#6B8E23' }} />
                              )}
                            </button>
                            {isOpen && (
                              <div className="px-4 pb-4 pt-0">
                                <p className="text-muted mb-0">{faq.answer}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="row justify-content-center mt-5">
            <div className="col-lg-8">
              <div className="card shadow border-0" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #556B1F 100%)' }}>
                <div className="card-body p-5 text-center text-white">
                  <h3 className="h4 fw-bold mb-3">Can't Find What You're Looking For?</h3>
                  <p className="mb-4">
                    If you have a question that isn't answered here, our team is ready to help. 
                    Contact us and we'll get back to you as soon as possible.
                  </p>
                  <a href="/contact" className="btn btn-light btn-lg fw-bold">Contact Support</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default FAQsPage;
